import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@shared/stores/authStore';
import { usePropertyStore } from '@shared/stores/propertyStore';
import { fetchMyProperties, type MemberProperty } from '@features/properties/api';
import {
  fetchMyUtilityLinks,
  fetchMyUtilityTypes,
  type MyUtilityLink,
  type UtilityTypeOption,
} from './api';

export interface ScopedUtilityLink extends MyUtilityLink {
  utilityName: string;
}

export interface UseScopedUtilityLinksResult {
  scopedLinks: ScopedUtilityLink[];
  activeProperty: MemberProperty | null;
  activePropertyId: string | null;
  utilityNameById: Map<string, string>;
  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
  refetch: () => void;
}

function scopeLinks(
  links: MyUtilityLink[],
  approvedLocationIds: Set<string>,
  targetLocationId: string | null,
  utilityNameById: Map<string, string>,
): ScopedUtilityLink[] {
  if (!targetLocationId) return [];

  return links
    .filter(
      (l) =>
        l.status === 'active' &&
        !!l.device_id &&
        approvedLocationIds.has(l.location_id) &&
        l.location_id === targetLocationId,
    )
    .map((l) => ({
      ...l,
      utilityName: utilityNameById.get(l.utility_type_id) ?? 'Utility',
    }));
}

/**
 * Utility links scoped to the logged-in community (tenant API) and the
 * member's active property (header property switcher).
 */
export function useScopedUtilityLinks(): UseScopedUtilityLinksResult {
  const memberId = useAuthStore((s) => s.user?.member_id);
  const tenantId = useAuthStore((s) => s.tenantId);
  const activePropertyId = usePropertyStore((s) => s.activePropertyId);
  const syncActiveProperty = usePropertyStore((s) => s.syncActiveProperty);

  const propertiesQuery = useQuery({
    queryKey: ['my-properties', memberId],
    queryFn: fetchMyProperties,
    enabled: !!memberId,
    staleTime: 60_000,
  });

  const linksQuery = useQuery({
    queryKey: ['my-utility-links', memberId, tenantId],
    queryFn: fetchMyUtilityLinks,
    enabled: !!memberId,
    staleTime: 60_000,
  });

  const typesQuery = useQuery({
    queryKey: ['my-utility-types', tenantId],
    queryFn: fetchMyUtilityTypes,
    staleTime: 5 * 60_000,
  });

  const properties = propertiesQuery.data ?? [];

  useEffect(() => {
    if (!memberId || !tenantId || properties.length === 0) return;
    syncActiveProperty(properties, { memberId, tenantId });
  }, [properties, memberId, tenantId, syncActiveProperty]);

  const utilityNameById = useMemo(() => {
    const map = new Map<string, string>();
    (typesQuery.data ?? []).forEach((t: UtilityTypeOption) => map.set(t.utility_type_id, t.name));
    return map;
  }, [typesQuery.data]);

  const activeProperty = useMemo(() => {
    if (properties.length === 0) return null;
    const match = activePropertyId
      ? properties.find((p) => p.property_id === activePropertyId)
      : undefined;
    return match ?? properties[0] ?? null;
  }, [properties, activePropertyId]);

  const targetLocationId = activeProperty?.property_id ?? null;

  const approvedLocationIds = useMemo(
    () => new Set(properties.map((p) => p.property_id)),
    [properties],
  );

  const scopedLinks = useMemo(
    () =>
      scopeLinks(
        linksQuery.data ?? [],
        approvedLocationIds,
        targetLocationId,
        utilityNameById,
      ),
    [linksQuery.data, approvedLocationIds, targetLocationId, utilityNameById],
  );

  const isLoading =
    propertiesQuery.isLoading || linksQuery.isLoading || typesQuery.isLoading;
  const isError = propertiesQuery.isError || linksQuery.isError;

  return {
    scopedLinks,
    activeProperty,
    activePropertyId: targetLocationId,
    utilityNameById,
    isLoading,
    isError,
    isEmpty: !isLoading && scopedLinks.length === 0,
    refetch: () => {
      void propertiesQuery.refetch();
      void linksQuery.refetch();
    },
  };
}
